import { styled } from '@linaria/react';
import type { FC } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentMinerEvent } from '../../../api/agent-miner-client/agent-miner-client.type';
import {
  inkDarkest,
  inkLight,
  inkLighter,
  primaryBase,
  primaryDarkest,
  purpleDarkest,
  purpleLightest,
  redBase,
  redDarkest,
  redLightest,
  secondaryDarkest,
  skyLightest,
  yellowLightest,
} from '../../../styles/design-tokens';
import {
  ARG_PREVIEW_MAX_LENGTH,
  asKeyedObject,
  AssistantMessageBlock,
  Avatar,
  Body,
  BodyText,
  clipSummaryValue,
  describeArgShape,
  formatJson,
  IndentRow,
  RoleLabel,
  Row,
  SubagentMessageBlock,
  summarizeRecord,
  ThinkingBlock,
  UserMessageBlock,
} from './event-block.helpers';
import { fmtNum } from './formatters';

interface EventBlockProps {
  event: AgentMinerEvent;
}

interface TodoBlockProps extends EventBlockProps {
  done: boolean;
}

// Metadata keys the backend writes onto TOOL_CALL/TOOL_ERROR events (see
// agent_miner/services/token_estimator.py). Both live inside the freeform
// `metadata` bag rather than as typed columns.
const TOOL_INPUT_ESTIMATED_TOKENS_KEY = 'tool_input_estimated_tokens';
const TOOL_OUTPUT_ESTIMATED_TOKENS_KEY = 'tool_output_estimated_tokens';

// A tool call whose combined in+out estimate reaches this flags a ⚠ on its
// collapsed chip so context-heavy calls are scannable without expanding.
const HEAVY_TOKEN_ESTIMATE_THRESHOLD = 10_000;

// These are character-heuristic payload-size estimates (~4 chars/token), NOT
// provider-measured or billable tokens. The tooltip keeps them from being read
// as the real per-turn token counts shown in the Overview and turn headers.
const TOKEN_ESTIMATE_TOOLTIP = 'Rough payload-size estimate (~4 chars/token), not billable model tokens.';

// Set by the backend on Claude Code edit tools (Edit/Write/MultiEdit), whose
// stored output is a full-file provenance blob the model never received. When
// true, the output estimate reflects the brief result instead — the note
// explains why it looks far smaller than the payload rendered below it.
const TOOL_OUTPUT_ESTIMATE_ADJUSTED_KEY = 'tool_output_estimate_adjusted';
const ADJUSTED_ESTIMATE_NOTE =
  'Estimate reflects the brief result the model received. The full payload below is stored for provenance and was not sent to the model.';

/**
 * Reads a non-negative token-estimate number out of an event's freeform
 * metadata bag, tolerating a missing key or a non-numeric value.
 *
 * @param {Record<string, unknown> | null} metadata - The event's `metadata` map, or null.
 * @param {string} key - The estimate key to read (e.g. `tool_input_estimated_tokens`).
 * @returns {number | null} The finite, non-negative numeric estimate, or null when absent/invalid.
 */
function readTokenEstimate(metadata: Record<string, unknown> | null, key: string): number | null {
  const value = metadata?.[key];
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * Decides whether a validated estimate is worth putting on screen.
 *
 * The backend writes BOTH estimate keys as soon as a tool event carries either
 * payload, and the estimate is `int(chars * 0.25)` — so a stored `0` means the
 * payload was absent or shorter than four characters, never a meaningful
 * measurement. Only a positive estimate renders, which keeps `~0 est.` off
 * no-argument tools and off a `Bash` call that produced no stdout.
 *
 * This is NOT sufficient on its own for a missing output: the claude_code edit
 * tools are stamped with a constant 9 rather than 0, so the caller must also
 * gate on whether an output was actually stored.
 *
 * Deliberately NOT a `estimate is number` type predicate: `0` is a valid
 * number that this returns false for, so narrowing here would tell a caller's
 * false branch that the value is `null` when it may be `0`.
 *
 * @param {number | null} estimate - A validated estimate from {@link readTokenEstimate}.
 * @returns {boolean} True when the estimate describes a payload worth showing.
 */
function hasPositiveEstimate(estimate: number | null): boolean {
  return estimate != null && estimate > 0;
}

/**
 * Renders one TOOL_CALL or TOOL_ERROR event as a collapsible row: the tool
 * signature with a preview of its first two arguments, a payload-size estimate
 * chip, and — once expanded — the full arguments and result or error.
 *
 * @param {AgentMinerEvent} event - The tool event to render; carries call args,
 *   result or error, and the backend's estimate metadata on the same record.
 * @returns {JSX.Element} The rendered tool-call row.
 */
function ToolCallBlock({ event }: EventBlockProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Per-instance so many tool rows never collide on one id.
  const estimateDisclaimerId = useId();
  // The wire type is `unknown` because this is an unvalidated JSONField: 2,180
  // rows in the backend's dev table store a bare string here, which made
  // `Object.entries` enumerate the characters as `tool(0="x", 1="x", …)`. The
  // full value is still shown by `formatJson` in the expanded Arguments block.
  const keyedArgs = asKeyedObject(event.tool_input);
  const argEntries = Object.entries(keyedArgs ?? {});
  // A non-null payload that is not a keyed object has no key=value preview.
  // Bare `Grep()` would claim the tool was called with no arguments while the
  // expanded Arguments block shows the real payload — but reusing the summary
  // helper here was worse: its `'ok'` give-up sentinel turned `0`, `42`,
  // `false` and `[]` into a fabricated argument named `ok`, and a crafted
  // payload could spell out `") · Bash(cmd="` and read as a second tool call in
  // what is an audit view. A shape descriptor says arguments exist without
  // promoting unvalidated wire text into this line's grammar; the values
  // themselves are one click away in the Arguments block.
  const nonKeyedArgSummary = keyedArgs != null ? null : describeArgShape(event.tool_input);
  const previewEntries = argEntries.slice(0, 2);
  const hiddenCount = argEntries.length - previewEntries.length;

  // BE merges PreToolUse + PostToolUse(Failure) into ONE event (see
  // hook_transformer_service._create_paired_event): a successful tool call
  // carries both tool_input AND tool_output on the same record; a failure
  // becomes TOOL_ERROR with tool_input + error_message. So the expanded view
  // reads call args and result from the same event — no pairing needed.
  const hasOutput = event.tool_output != null;
  // Trimmed-or-null rather than `!= null`: the hook transformer writes `""` for
  // a failure that reported no reason (`_extract(hook, 'payload.reason', '')`),
  // and `??` does not fall through on an empty string — so the Error section
  // rendered with a blank body and the `Unknown error` fallback was dead code.
  const errorText = event.error_message?.trim() || event.error_type?.trim() || null;
  const hasErrorMessage = errorText != null;
  // Error text alone promotes the row to an error ONLY when there is no output
  // to show. An event carrying just `error_type` used to render under a
  // `Result` heading with a body of `(none)`, hiding the one field that said
  // what went wrong — but treating any error text as decisive went too far the
  // other way and dropped a real `tool_output` whenever `error_type` was also
  // set, while the estimate chip still described the output that was no longer
  // rendered.
  const isErrorEvent = (event.event_type ?? '').toUpperCase() === 'TOOL_ERROR' || event.status === 'ERROR';
  const isError = isErrorEvent || (!hasOutput && hasErrorMessage);
  const resultLabel = isError ? 'Error' : 'Result';
  const resultBody = isError ? (errorText ?? 'Unknown error') : formatJson(event.tool_output);

  const inputEstimate = readTokenEstimate(event.metadata, TOOL_INPUT_ESTIMATED_TOKENS_KEY);
  // Only an event that actually stored an output has an output size to report.
  // The backend's claude_code branch stamps a CONSTANT success-string estimate
  // (9 tokens) keyed on `tool_name` alone — it never checks `status`,
  // `event_type` or `tool_output`. That is no longer the merged backend: it
  // now also requires `stored_output is not None and not
  // isinstance(stored_output, str)`, so a null output takes the `else` branch
  // and is written as `0`, which `hasPositiveEstimate` already suppresses. The
  // gate below is therefore redundant against current data and kept only as
  // armour for rows written before that landed — do not reason from this
  // paragraph as though the backend were still unguarded.
  // (token_estimator.add_tool_token_estimates),
  // and `configs/claude_code.json` maps PostToolUseFailure to `error_message`
  // with no `tool_output`. So a FAILED Edit/Write/MultiEdit/NotebookEdit
  // arrives claiming a 9-token output that never existed, and a successful one
  // whose output was not stored arrives with a size the Result section never
  // renders. Gating on `hasOutput` rejects both, so the collapsed chip and the
  // expanded per-side figures are derived from the same values. Note the
  // DISPLAYED strings still need not add up: `fmtNum` abbreviates each
  // independently, so 1050 + 1050 shows as `~1.1k` + `~1.1k` under a `~2.1k`
  // chip.
  // `!isError` as well as `hasOutput`: an error row shows the error text as
  // its body, so a stored `tool_output` is never rendered and reporting its
  // size would label something the reader cannot see.
  const outputEstimate =
    hasOutput && !isError ? readTokenEstimate(event.metadata, TOOL_OUTPUT_ESTIMATED_TOKENS_KEY) : null;
  const totalEstimate =
    inputEstimate != null || outputEstimate != null ? (inputEstimate ?? 0) + (outputEstimate ?? 0) : null;
  const isHeavyEstimate = totalEstimate != null && totalEstimate >= HEAVY_TOKEN_ESTIMATE_THRESHOLD;
  const outputEstimateAdjusted = hasOutput && event.metadata?.[TOOL_OUTPUT_ESTIMATE_ADJUSTED_KEY] === true;

  return (
    <Row>
      <Avatar bg={isError ? redLightest : skyLightest} fg={isError ? redDarkest : inkDarkest} square>
        {isError ? '!' : '⚙'}
      </Avatar>
      <Body>
        {/* The disclaimer is a DESCRIPTION on the toggle, not a name fragment:
            `aria-label` on the chip span is naming-prohibited on role=generic AND
            accname folds a descendant's label into the button's own name, so it
            repeated on every row. A description is announced after the name, is
            permitted on any role, and — unlike `title` — reaches keyboard and
            touch users. Rendered as a SIBLING of the toggle so it cannot be
            walked back into the name by content recursion. */}
        <ToolCallToggle
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-describedby={hasPositiveEstimate(totalEstimate) ? estimateDisclaimerId : undefined}
        >
          <ToolCallChevron open={expanded} aria-hidden="true">
            ▶
          </ToolCallChevron>
          <ToolCallLine>
            <ToolCallSignature>
              <ToolName>{event.tool_name ?? 'tool'}</ToolName>
              {/* The parens belong to the preview, so they are gated with it:
                  keeping them while dropping the args made the expanded
                  accessible name read `Grep()` — "called with no arguments" —
                  next to an Arguments block showing the real ones. */}
              {!expanded && <Punct>(</Punct>}
              {!expanded && nonKeyedArgSummary != null && <ToolArgValue>{nonKeyedArgSummary}</ToolArgValue>}
              {!expanded &&
                previewEntries.map(([k, v], index) => (
                  <span key={k}>
                    {index > 0 && <Punct>, </Punct>}
                    <ToolArgKey>{k}=</ToolArgKey>
                    <ToolArgValue>
                      {typeof v === 'string'
                        ? `"${clipSummaryValue(v, ARG_PREVIEW_MAX_LENGTH)}"`
                        : clipSummaryValue(JSON.stringify(v) ?? String(v), ARG_PREVIEW_MAX_LENGTH)}
                    </ToolArgValue>
                  </span>
                ))}
              {!expanded && hiddenCount > 0 && <Punct>, …+{hiddenCount}</Punct>}
              {!expanded && <Punct>)</Punct>}
              {/* Not `!expanded`: the row is still an error once opened, and
                  dropping this deleted "error" from the accessible name exactly
                  when the error body appeared. */}
              {isError && <ErrorHint> · error</ErrorHint>}
            </ToolCallSignature>
            {!expanded && hasPositiveEstimate(totalEstimate) && (
              <TokenEstimateChip heavy={isHeavyEstimate} title={TOKEN_ESTIMATE_TOOLTIP}>
                ~{fmtNum(totalEstimate)} est.
                {isHeavyEstimate && (
                  <>
                    <span aria-hidden="true"> ⚠</span>
                    <VisuallyHidden>, context-heavy</VisuallyHidden>
                  </>
                )}
              </TokenEstimateChip>
            )}
          </ToolCallLine>
          {event.wrote_merged_code && (
            <MergedChip title={t('agent_miner.pull_requests.merged_code_tooltip')}>
              {t('agent_miner.pull_requests.merged_code')}
            </MergedChip>
          )}
        </ToolCallToggle>
        {hasPositiveEstimate(totalEstimate) && (
          <VisuallyHidden id={estimateDisclaimerId} aria-hidden="true">
            {TOKEN_ESTIMATE_TOOLTIP}
          </VisuallyHidden>
        )}
        {expanded && (
          <ToolCallDetails>
            <DetailSection>
              <DetailHeader>
                <DetailLabel>Arguments</DetailLabel>
                {hasPositiveEstimate(inputEstimate) && (
                  <DetailEstimate title={TOKEN_ESTIMATE_TOOLTIP}>
                    ~{fmtNum(inputEstimate)} est. tokens <span aria-hidden="true">ⓘ</span>
                  </DetailEstimate>
                )}
              </DetailHeader>
              <DetailPre>{formatJson(event.tool_input)}</DetailPre>
            </DetailSection>
            {/* `isError` rather than `hasErrorMessage`: a TOOL_ERROR (or status
                ERROR) event whose tool_output, error_message AND error_type are
                all null would otherwise show the error indicator on the row and
                then hide the section entirely, making `resultBody`'s
                `Unknown error` fallback unreachable. */}
            {(hasOutput || isError) && (
              <DetailSection>
                <DetailHeader>
                  <DetailLabel error={isError}>{resultLabel}</DetailLabel>
                  {hasPositiveEstimate(outputEstimate) && (
                    <DetailEstimate title={TOKEN_ESTIMATE_TOOLTIP}>
                      ~{fmtNum(outputEstimate)} est. tokens <span aria-hidden="true">ⓘ</span>
                    </DetailEstimate>
                  )}
                </DetailHeader>
                {!isError && outputEstimateAdjusted && <DetailNote>{ADJUSTED_ESTIMATE_NOTE}</DetailNote>}
                <DetailPre error={isError}>{resultBody}</DetailPre>
              </DetailSection>
            )}
          </ToolCallDetails>
        )}
      </Body>
    </Row>
  );
}

function ToolResultBlock({ event }: EventBlockProps) {
  return (
    <IndentRow>
      <ResultPill>
        <span aria-hidden="true">↳ </span>
        {summarizeRecord(event.tool_output)}
      </ResultPill>
    </IndentRow>
  );
}

function ErrorBlock({ event }: EventBlockProps) {
  return (
    <IndentRow>
      <ErrorPill>
        <span aria-hidden="true">↳ </span>
        error: {event.error_message?.trim() || event.error_type?.trim() || 'Unknown error'}
      </ErrorPill>
    </IndentRow>
  );
}

function GenericEventBlock({ event }: EventBlockProps) {
  // Falsy-or, not `??`: an event with `content: ''` and a real `error_message`
  // would otherwise render an empty body and swallow the error.
  const body = event.content?.trim() || event.error_message?.trim() || null;
  return (
    <Row>
      <Avatar bg={skyLightest} fg={inkDarkest} square>
        •
      </Avatar>
      <Body>
        <RoleLabel>{event.event_type}</RoleLabel>
        {body && <BodyText>{body}</BodyText>}
      </Body>
    </Row>
  );
}

function TodoBlock({ event, done }: TodoBlockProps) {
  return (
    <Row>
      <Avatar bg={skyLightest} fg={inkDarkest} square>
        {done ? '☑' : '☐'}
      </Avatar>
      <Body>
        <RoleLabel>{done ? 'To-do completed' : 'To-do created'}</RoleLabel>
        {event.content && <BodyText>{event.content}</BodyText>}
      </Body>
    </Row>
  );
}

const TodoCreated = ({ event }: EventBlockProps) => <TodoBlock event={event} done={false} />;
const TodoCompleted = ({ event }: EventBlockProps) => <TodoBlock event={event} done={true} />;

const SubagentStart = ({ event }: EventBlockProps) => <SubagentMessageBlock event={event} label="Subagent started" />;
const SubagentStop = ({ event }: EventBlockProps) => <SubagentMessageBlock event={event} label="Subagent finished" />;
const SubagentSpawn = ({ event }: EventBlockProps) => <SubagentMessageBlock event={event} label="Subagent spawned" />;

// Normalized event_type → renderer. BE event_type values are UPPERCASE (see
// healthapi/apps/agent_miner/choices.py); EventBlock normalizes before lookup.
// TOOL_CALL and TOOL_ERROR both route to ToolCallBlock because the hook
// transformer merges call+result (or call+error) onto a single event record.
const EVENT_RENDERERS: Record<string, FC<EventBlockProps>> = {
  USER_MESSAGE: UserMessageBlock,
  RESPONSE: AssistantMessageBlock,
  ASSISTANT_MESSAGE: AssistantMessageBlock,
  THINKING: ThinkingBlock,
  PLANNING: ThinkingBlock,
  TOOL_CALL: ToolCallBlock,
  TOOL_ERROR: ToolCallBlock,
  TOOL_RESULT: ToolResultBlock,
  ERROR: ErrorBlock,
  SUBAGENT_ERROR: ErrorBlock,
  SUBAGENT_START: SubagentStart,
  SUBAGENT_STOP: SubagentStop,
  SUBAGENT_SPAWN: SubagentSpawn,
  TODO_CREATED: TodoCreated,
  TODO_COMPLETED: TodoCompleted,
};

/**
 * Renders a single Agent Miner transcript event, dispatching on its
 * `event_type` to the matching renderer.
 *
 * Backend `event_type` values are UPPERCASE and are normalized before lookup.
 * A generic `MESSAGE` event splits on `role` instead, since the renderer map is
 * keyed by event type; anything unrecognised falls back to the error pill when
 * its status says ERROR, and to the generic block otherwise.
 *
 * @param {AgentMinerEvent} event - The event to render.
 * @returns {JSX.Element} The renderer chosen for this event.
 */
export function EventBlock({ event }: EventBlockProps) {
  const eventType = (event.event_type ?? '').toUpperCase();

  // Role-based fallback: handle generic MESSAGE events first, since the map
  // keys are event_type values and MESSAGE splits on `role`.
  if (eventType === 'MESSAGE') {
    if (event.role === 'USER') return <UserMessageBlock event={event} />;
    if (event.role === 'ASSISTANT') return <AssistantMessageBlock event={event} />;
  }

  const Renderer = EVENT_RENDERERS[eventType];
  if (Renderer) return <Renderer event={event} />;

  // Any event with status === 'ERROR' and no specific renderer falls back to
  // the generic error pill (e.g. unknown event types flagged as errors).
  if (event.status === 'ERROR') return <ErrorBlock event={event} />;

  return <GenericEventBlock event={event} />;
}

const ToolCallToggle = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${skyLightest};
  }
`;

const ToolCallChevron = styled.span<{ open: boolean }>`
  color: ${inkLight};
  font-size: 9px;
  line-height: 1.6;
  transform: ${(props) => (props.open ? 'rotate(90deg)' : 'none')};
  transition: transform 0.12s ease;
  flex-shrink: 0;
`;

const ToolCallLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font: 600 12px/1.4 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkDarkest};
  min-width: 0;
  flex: 1;
  word-break: break-word;
`;

const ToolCallSignature = styled.span`
  min-width: 0;
  flex: 1;
  word-break: break-word;
`;

const TokenEstimateChip = styled.span<{ heavy?: boolean }>`
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 999px;
  background: ${(props) => (props.heavy ? yellowLightest : skyLightest)};
  color: ${(props) => (props.heavy ? inkDarkest : inkLight)};
  font: 500 10.5px/1.4 'Figtree', sans-serif;
  white-space: nowrap;
`;

const ToolCallDetails = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const DetailLabel = styled.div<{ error?: boolean }>`
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${(props) => (props.error ? redDarkest : inkLight)};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailEstimate = styled.span`
  font: 500 10px/1 'Figtree', sans-serif;
  color: ${inkLight};
  white-space: nowrap;
`;

const DetailNote = styled.p`
  margin: 0;
  font: 400 10.5px/1.4 'Figtree', sans-serif;
  color: ${inkLight};
  font-style: italic;
`;

/**
 * Text for assistive technology only. Carries meaning that the visual design
 * conveys with a glyph alone, so the ⚠ on a context-heavy chip is not the
 * sole signal (WCAG 1.4.1). Mirrors the helper in `pr-detail.helpers`.
 */
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const DetailPre = styled.pre<{ error?: boolean }>`
  margin: 0;
  padding: 8px 10px;
  background: ${(props) => (props.error ? redLightest : skyLightest)};
  border-radius: 4px;
  border-left: 2px solid ${(props) => (props.error ? redBase : primaryBase)};
  font: 400 11.5px/1.5 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${(props) => (props.error ? redDarkest : inkDarkest)};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 360px;
  overflow: auto;
`;

const ToolName = styled.span`
  color: ${secondaryDarkest};
`;

const MergedChip = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 99px;
  background: ${purpleLightest};
  color: ${purpleDarkest};
  font: 600 10px/1.3 'Figtree', sans-serif;
  white-space: nowrap;
`;

const ToolArgKey = styled.span`
  color: ${inkLight};
`;

const ToolArgValue = styled.span`
  color: ${primaryDarkest};
`;

const Punct = styled.span`
  color: ${inkLighter};
`;

const ErrorHint = styled.span`
  color: ${redDarkest};
  font-weight: 500;
`;

const ResultPill = styled.div`
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  background: ${skyLightest};
  border-radius: 4px;
  border-left: 2px solid ${primaryBase};
  font: 400 11.5px/1.4 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkDarkest};
  word-break: break-word;
`;

const ErrorPill = styled.div`
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  background: ${redLightest};
  border-radius: 4px;
  border-left: 2px solid ${redBase};
  font: 400 11.5px/1.4 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${redDarkest};
  word-break: break-word;
`;
